import React from 'react';
import { motion } from 'framer-motion';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
interface Example {
  src: string;
  alt: string;
  brand: string;
  title: string;
  span: string;
  kind?: string;
  highlight?: boolean;
}
const EXAMPLES: Example[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/v8fWYPFpJwfo81j5rqFNbs/Screenshot_2026-09-10_at_15.12.47.png',
  alt: 'Amoxil teaser Snak opening on a mother and child with the line “Two nights without sleep. Seven minutes to decide.”',
  brand: 'Amoxil',
  title: 'Story-led teaser opening',
  span: 'lg:col-span-2 lg:row-span-2',
  highlight: true
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2WDtchn43w8oePYBwCWGRa/Screenshot_2026-09-11_at_10.23.04.png',
  alt: 'Augmentin teaser Snak landing screen “Healing everyday heroes” with product information and a prescribing-information QR code',
  brand: 'Augmentin',
  title: 'Healing everyday heroes',
  span: 'lg:col-span-5'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/kmYpMVEekGYnUUaoYZokDA/Screenshot_2026-09-16_at_12.47.30.png',
  alt: 'Hemlibra challenge Snak asking “Are you ready for a challenge?” and inviting the HCP into live polls to compare answers with peers',
  brand: 'Hemlibra',
  title: 'Live poll challenge',
  span: 'lg:col-span-5',
  kind: 'Challenge'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/qHf7fg4CkUPdkV7VtH9hES/Screenshot_2026-09-16_at_12.47.12.png',
  alt: 'Focus areas Snak menu letting an HCP select Lung, Hematology, Breast, Liver or Pipeline to explore',
  brand: 'Roche',
  title: 'Focus area selection',
  span: 'lg:col-span-7',
  kind: 'Navigator'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/iTZ3R35eg4oc4Hqw5rYsvD/Screenshot_2026-09-16_at_12.47.20.png',
  alt: 'Vabysmo product Snak entry screen with routes into 3Ds, real-world data and safety',
  brand: 'Vabysmo',
  title: 'Product deep dive',
  span: 'lg:col-span-3',
  kind: 'Deep dive'
}];
export function SlideSnakExamples({}: SlideProps) {
  return <InteriorSlide surface data-id="element-598" data-mp="3jtqsmfv16ru5">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(0.6rem,1.3vw,1.25rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-599" data-mp="3jujp8o0a00yk">
        <div data-id="element-600" data-mp="anscb9qtqusp">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-601" data-mp="an8uru5368f7">
            Teaser examples
          </p>
          <h2 className="text-[clamp(1.8rem,3vw,2.9rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-602" data-mp="6km8s9v3jhpx">
            What a teaser Snak looks like{' '}
            <span className="text-[var(--brand)]" data-id="element-603" data-mp="ffitnzo6iitm">in practice</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-604" data-mp="ucj8j2l9ujao">
          Brand-compliant and fully referenced, on phone and on desktop.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-[clamp(0.5rem,1vw,1rem)] sm:grid-cols-2 lg:grid-cols-12 lg:grid-rows-2" data-id="element-605" data-mp="18zi336ffdttm">
        {EXAMPLES.map((example, index) => <motion.figure key={example.src} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.14 + index * 0.09,
        duration: 0.5
      }} className={`flex min-h-0 flex-col overflow-hidden rounded-2xl border bg-white p-[clamp(0.4rem,0.7vw,0.7rem)] shadow-sm ${example.span} ${example.highlight ? 'border-[var(--brand)]/40 ring-1 ring-[var(--brand)]/15' : 'border-[var(--fg)]/8'}`} data-id="element-606" data-mp="yzr7t2j1jash" data-mpi={index}>
            <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-xl bg-[var(--surface)]" data-id="element-607" data-mp="l6rtv4ywpon1">
              <img src={example.src} alt={example.alt} className="h-full w-full object-contain" data-id="element-608" data-mp="bb7ycjo98ryt" />
            </div>

            <figcaption className="flex flex-shrink-0 items-baseline justify-between gap-2 pt-2" data-id="element-609" data-mp="19n12dxqpf66v">
              <div className="min-w-0" data-id="element-610" data-mp="14vssl0lgpit6">
                <p className="truncate text-[clamp(0.7rem,0.95vw,0.88rem)] font-extrabold leading-tight text-[var(--fg)]" data-id="element-611" data-mp="14urcuiti4854">
                  {example.title}
                </p>
                <p className="mt-0.5 truncate text-[9px] font-bold uppercase tracking-[0.14em] text-[var(--muted)]" data-id="element-612" data-mp="uy4txzkqjr5q">
                  {example.brand}
                </p>
              </div>
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${example.highlight ? 'bg-[var(--brand)] text-white' : 'bg-[var(--brand)]/10 text-[var(--brand)]'}`} data-id="element-613" data-mp="lwol3w9kjx3m">
                {example.kind ?? 'Teaser'}
              </span>
            </figcaption>
          </motion.figure>)}
      </div>
    </InteriorSlide>;
}